import { useEffect, useLayoutEffect } from 'react';

/**
 * Let a scroll box reach the bottom of the window.
 *
 * Why (Max, 2026-10-20, on SAP Ship Doc: a band of empty page under a table that stopped at 410px):
 * a fixed max-height is right for exactly one header. The rows above the box come and go — a sync
 * note, an error, a toolbar that wraps on a narrower screen — so the box is measured where it
 * actually starts and given the rest of the window, not a number guessed for one layout.
 *
 * The top is the box's place on the PAGE (its screen top plus whatever its scrolling ancestors have
 * scrolled), so a box measured while the page is scrolled still gets the height it would have at
 * the top. A box that is not drawn (display: none) is left alone and measured when it is.
 *
 *   const box = useRef(null);  useFillHeight(box, [view, rows.length > 0]);
 */
export default function useFillHeight(ref, deps = [], { gap = 18, min = 260 } = {}) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const fit = () => {
      if (!el.offsetParent) return;
      let top = el.getBoundingClientRect().top;
      for (let p = el.parentElement; p; p = p.parentElement) top += p.scrollTop || 0;
      el.style.maxHeight = `${Math.max(min, Math.floor(window.innerHeight - top - gap))}px`;
    };
    fit();
    window.addEventListener('resize', fit);
    /* A toolbar that wraps once its vendor list arrives, a note that appears after a sync: the box
       moves down without the window resizing. Watching the parent catches both — the fit that
       follows changes nothing the second time, so it settles. */
    const ro = typeof ResizeObserver !== 'undefined' && el.parentElement ? new ResizeObserver(() => window.requestAnimationFrame(fit)) : null;
    if (ro) ro.observe(el.parentElement);
    return () => { window.removeEventListener('resize', fit); if (ro) ro.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  /* The row-window hook reads the box's height on resize; tell it the box changed. */
  useEffect(() => {
    if (ref.current && ref.current.offsetParent) ref.current.dispatchEvent(new Event('scroll'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
